import { ReactNode } from 'react';

import { attachComponentData } from '@backstage/core-plugin-api';

import { isNotFoundError } from '@spotify/confidence-graphql';

import { usePageBridgeProps } from '../../../utils';
import { PAGE_WRAPPER_KEY } from '../../composition';
import { ResourceErrorPage } from './ResourceErrorPage';
import { ResourceLoadingPage } from './ResourceLoadingPage';
import { ResourceNotFoundErrorPage } from './ResourceNotFoundErrorPage';

export type ResourceGuardProps = {
  name?: string;
  label?: string;
  loading?: boolean;
  loadingFallback?: ReactNode;
  error?: Error | null;
};

const BridgedResourceGuard = ({
  name,
  label,
  loadingFallback,
  error,
  loading,
  children,
}: ResourceGuardProps & { children: ReactNode }) => {
  const { descriptor } = usePageBridgeProps();
  const resolvedLabel = label ?? descriptor.title;

  if (error) {
    if (isNotFoundError(error)) {
      return <ResourceNotFoundErrorPage />;
    }

    return (
      <ResourceErrorPage
        title={`Failed to load ${resolvedLabel}`}
        error={`Error loading ${name ?? resolvedLabel}: ${error.message ?? 'Unknown error'}`}
      />
    );
  }

  if (loading) {
    return <>{loadingFallback ?? <ResourceLoadingPage />}</>;
  }

  return <>{children}</>;
};

export const ResourceGuard = (_props: ResourceGuardProps): null => null;

attachComponentData(ResourceGuard, PAGE_WRAPPER_KEY, BridgedResourceGuard);
